import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AppProvider, useApp, ROLE_LABELS } from '@/context/AppContext';
import { Sidebar } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import { BusinessFilter } from '@/components/layout/BusinessFilter';
import Dashboard from '@/pages/Dashboard';
import TeamManagement from '@/pages/TeamManagement';
import ClientVisits from '@/pages/ClientVisits';
import BusinessPlaces from '@/pages/BusinessPlaces';
import SalesPoints from '@/pages/SalesPoints';
import Performance from '@/pages/Performance';
import VisitPlanner from '@/pages/VisitPlanner';
import UserTree from '@/pages/UserTree';
import Approvals from '@/pages/Approvals';
import DataImport from '@/pages/DataImport';
import AdminPanel from '@/pages/AdminPanel';
import TravelPlan from '@/pages/TravelPlan';
import TravelApprovals from '@/pages/TravelApprovals';
import ApiDocs from '@/pages/ApiDocs';
import Leads from '@/pages/Leads';
import ExpenseClaims from '@/pages/ExpenseClaims';

const pageTitles = {
  '/': 'Dashboard',
  '/team': 'Team Management',
  '/org-tree': 'Organization Tree',
  '/visits': 'Client Visits',
  '/places': 'Business Places',
  '/approvals': 'Approvals',
  '/sales-points': 'Sales Points',
  '/performance': 'Performance',
  '/planner': 'Visit Planner',
  '/import': 'Data Import',
  '/admin': 'Admin Panel',
  '/travel-plans': 'Travel Plans',
  '/travel-approvals': 'Travel Approvals',
  '/leads': 'Leads',
  '/expense-claims': 'Expense Claims',
  '/docs': 'API Documentation',
};

const AppContent = () => {
  const { currentUser, setCurrentUser, seedData, seeded, api } = useApp();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authState, setAuthState] = useState('loading'); // loading | authenticated | denied
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  // Validate session token from URL on mount
  useEffect(() => {
    const validateSession = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionToken = params.get('session_token');

      if (!sessionToken) {
        setAuthState('denied');
        return;
      }

      try {
        if (!seeded) await seedData();
        const res = await api.validateSession(sessionToken);
        setCurrentUser(res.data);
        setAuthState('authenticated');
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      } catch (e) {
        setAuthState('denied');
      }
    };

    if (!currentUser) {
      validateSession();
    } else {
      setAuthState('authenticated');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (authState === 'loading') {
    return (
      <div data-testid="auth-loading" className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-400">Validating session...</p>
        </div>
      </div>
    );
  }

  if (authState === 'denied' || !currentUser) {
    return (
      <div data-testid="access-denied" className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h1 data-testid="access-denied-title" className="text-2xl font-extrabold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            This application can only be accessed through the authorized portal. Please use the platform to open this dashboard.
          </p>
        </div>
      </div>
    );
  }

  const title = pageTitles[location.pathname] || 'Dashboard';

  return (
    <div className="min-h-screen bg-neutral-50">
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <main className={`transition-all duration-300 ${isMobile ? 'ml-0' : collapsed ? 'ml-20' : 'ml-64'}`}>
        <TopBar title={title} onMenuClick={() => setMobileOpen(true)} isMobile={isMobile} />
        <div className={`${isMobile ? 'px-4 pt-3 pb-4' : 'px-8 pt-4 pb-8'}`}>
          <div className="mb-4">
            <BusinessFilter />
          </div>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/team" element={<TeamManagement />} />
            <Route path="/visits" element={<ClientVisits />} />
            <Route path="/places" element={<BusinessPlaces />} />
            <Route path="/sales-points" element={<SalesPoints />} />
            <Route path="/performance" element={<Performance />} />
            <Route path="/planner" element={<VisitPlanner />} />
            <Route path="/org-tree" element={<UserTree />} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/import" element={<DataImport />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/travel-plans" element={<TravelPlan />} />
            <Route path="/travel-approvals" element={<TravelApprovals />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/expense-claims" element={<ExpenseClaims />} />
            <Route path="/docs" element={<ApiDocs />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <AppContent />
        <Toaster position="top-right" richColors />
      </AppProvider>
    </BrowserRouter>
  );
}

export default App;
